import React from "react";
import { Alert, Input, Modal, message } from "antd";
import { Formik } from "formik";
import APIService from "../../Service/APIService";
import Text from "../../common/typography/text.js";
import {
  ACCOUNT_CHANGE_REQUEST_FIELDS,
  ACCOUNT_CHANGE_REQUEST_TABS,
  MAX_REQUESTER_NOTES_LENGTH,
  REQUESTER_NOTES_FIELD,
  accountChangeRequestValidationSchema,
  buildAccountChangeInitialValues,
  diffAccountChangeValues,
  normalizeRequesterNotes,
} from "./accountChangeRequestFields.js";
import {
  buildStateLookup,
  getDerivedFieldFlags,
} from "../../utils/stateDerivedFields.js";
import "./RequestChangesModal.css";

// Medicare Admin Contractor follows the shipping State via StatesMaster; when
// the state has a mapping the reviewer's approval applies the mapped value, so
// requesting a different one is pointless — the field is locked with a note.
const MAC_DERIVED_HELP =
  "Set automatically from the shipping state; it cannot be requested here.";

const RequestChangesModal = ({ open, account, onCancel, onSubmitted }) => {
  const [activeTab, setActiveTab] = React.useState("shipping");
  const [serverError, setServerError] = React.useState("");
  const apiRef = React.useRef(new APIService());
  const initialValues = React.useMemo(
    () => buildAccountChangeInitialValues(account),
    [account]
  );

  const [stateRows, setStateRows] = React.useState([]);
  React.useEffect(() => {
    if (!open) return undefined;
    let active = true;
    // Advisory only: without the rows every field stays editable and the
    // backend still enforces the mapping on approval.
    Promise.resolve()
      .then(() => new APIService().getDataFromBackend("getstatesmaster"))
      .then((response) => {
        if (active) setStateRows(response?.data?.data || []);
      })
      .catch(() => {
        if (active) setStateRows([]);
      });
    return () => {
      active = false;
    };
  }, [open]);
  const stateLookup = React.useMemo(() => buildStateLookup(stateRows, []), [stateRows]);

  React.useEffect(() => {
    if (open) {
      setActiveTab("shipping");
      setServerError("");
    }
  }, [open]);

  const submit = async (values, helpers) => {
    setServerError("");
    const changes = diffAccountChangeValues(account, values);
    if (!Object.keys(changes).length) {
      setServerError("Change at least one field before submitting.");
      helpers.setSubmitting(false);
      return;
    }

    try {
      const requesterNotes = normalizeRequesterNotes(
        values[REQUESTER_NOTES_FIELD]
      );
      const response = await apiRef.current.addDataToBackend(
        {
          AccountID: Number(account.ID),
          Changes: changes,
          ...(requesterNotes ? { RequesterNotes: requesterNotes } : {}),
        },
        "requestaccountchanges"
      );
      message.success("Your account changes were submitted for review.");
      onSubmitted?.(response?.data?.data);
    } catch (error) {
      const code = error?.response?.data?.code;
      const errorMessage =
        error?.response?.data?.message || "Unable to submit account changes.";
      setServerError(errorMessage);
      if (code === "ACCOUNT_UPDATE_REQUEST_PENDING") {
        onSubmitted?.({
          ID: error?.response?.data?.requestId,
          AccountID: Number(account.ID),
          Status: "Pending",
        });
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      footer={null}
      width={680}
      centered
      destroyOnClose
      onCancel={onCancel}
      title={null}
      className="request-changes-modal-shell"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={accountChangeRequestValidationSchema}
        enableReinitialize
        onSubmit={submit}
      >
        {(formik) => (
          <form className="request-changes-modal" onSubmit={formik.handleSubmit}>
            <div className="request-changes-header">
              <div>
                <Text variant="h1">Request Account Changes</Text>
                <p>Changes will not affect the account until an administrator approves them.</p>
              </div>
            </div>

            {serverError ? <Alert type="error" showIcon message={serverError} /> : null}

            <div className="request-changes-tabs" role="tablist">
              {ACCOUNT_CHANGE_REQUEST_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  id={`request-changes-tab-${tab.key}`}
                  aria-controls="request-changes-tabpanel"
                  aria-selected={activeTab === tab.key}
                  className={activeTab === tab.key ? "active" : ""}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div
              id="request-changes-tabpanel"
              role="tabpanel"
              aria-labelledby={`request-changes-tab-${activeTab}`}
              className={`request-changes-grid${
                activeTab === "notes" ? " request-changes-notes-grid" : ""
              }`}
            >
              {activeTab === "notes" ? (
                <div className="request-changes-field request-changes-notes-field">
                  <label htmlFor="request-RequesterNotes">
                    Notes for reviewer <span className="optional">(optional)</span>
                  </label>
                  <Input.TextArea
                    id="request-RequesterNotes"
                    name={REQUESTER_NOTES_FIELD}
                    value={formik.values[REQUESTER_NOTES_FIELD] ?? ""}
                    rows={6}
                    maxLength={MAX_REQUESTER_NOTES_LENGTH}
                    showCount
                    placeholder="Add any context that may help the reviewer."
                    disabled={formik.isSubmitting}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  {formik.touched[REQUESTER_NOTES_FIELD] &&
                  formik.errors[REQUESTER_NOTES_FIELD] ? (
                    <div className="request-changes-error">
                      {formik.errors[REQUESTER_NOTES_FIELD]}
                    </div>
                  ) : null}
                </div>
              ) : (
                ACCOUNT_CHANGE_REQUEST_FIELDS.filter(
                  (field) => field.tab === activeTab
                ).map((field) => {
                  const macLocked =
                    field.key === "MedicareAdminContractor" &&
                    getDerivedFieldFlags(stateLookup, formik.values.State)
                      .MedicareAdminContractor;
                  return (
                  <div className="request-changes-field" key={field.key}>
                    <label htmlFor={`request-${field.key}`}>
                      {field.label}
                      {field.required ? <span>*</span> : null}
                    </label>
                    <Input
                      id={`request-${field.key}`}
                      name={field.key}
                      value={formik.values[field.key] ?? ""}
                      maxLength={255}
                      disabled={formik.isSubmitting || macLocked}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    />
                    {macLocked ? (
                      <div className="request-changes-help">{MAC_DERIVED_HELP}</div>
                    ) : null}
                    {formik.touched[field.key] && formik.errors[field.key] ? (
                      <div className="request-changes-error">
                        {formik.errors[field.key]}
                      </div>
                    ) : null}
                  </div>
                  );
                })
              )}
            </div>

            <div className="request-changes-footer">
              <button type="button" className="account-secondary-button" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="submit"
                className="account-save-button"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default RequestChangesModal;
